/**
 * Mapping utilities between frontend filter format and backend parsed_json.properties_data format
 */

/**
 * Convert frontend filters to backend parsed_json.properties_data format
 * Frontend: { priceMin, priceMax, bedrooms, propertyTypes: ['villa', 'flat'], features: ['pool'] }
 * Backend: { price_from, price_to, bedrooms_from, types: ['villa', 'apartment'], characteristics: { must_have: ['swimming_pool'] } }
 */
export const frontendToBackendFilters = (frontendFilters) => {
  if (!frontendFilters) return {};

  const backendFilters = {};

  // Location mapping
  if (frontendFilters.location) {
    backendFilters.location = frontendFilters.location;
  }

  // Price mapping
  if (frontendFilters.priceMin) {
    backendFilters.price_from = Number(frontendFilters.priceMin);
  }
  if (frontendFilters.priceMax) {
    backendFilters.price_to = Number(frontendFilters.priceMax);
  }

  // Bedrooms mapping
  if (frontendFilters.bedrooms) {
    backendFilters.bedrooms_from = Number(frontendFilters.bedrooms);
  }

  // Bathrooms mapping
  if (frontendFilters.bathrooms) {
    backendFilters.bathrooms_from = Number(frontendFilters.bathrooms);
  }

  // Area mapping
  if (frontendFilters.areaMin) {
    backendFilters.total_area_from = Number(frontendFilters.areaMin);
  }
  if (frontendFilters.areaMax) {
    backendFilters.total_area_to = Number(frontendFilters.areaMax);
  }

  // Property types mapping: 'flat' → 'apartment', keep others as-is
  if (frontendFilters.propertyTypes && frontendFilters.propertyTypes.length > 0) {
    backendFilters.types = frontendFilters.propertyTypes.map((type) => {
      if (type === 'flat') return 'apartment';
      return type; // villa, house, townhouse, penthouse stay the same
    });
  }

  // Conditions mapping: TEMPORARILY DISABLED
  // if (frontendFilters.conditions && frontendFilters.conditions.length > 0) {
  //   console.log('🔧 Frontend conditions:', frontendFilters.conditions);
  //
  //   const conditionMapping = {
  //     'new': 'new',
  //     'excellent': 'very-good',
  //     'good': 'used',
  //     'needs_renovation': 'ruin'
  //   };
  //
  //   backendFilters.conditions = frontendFilters.conditions.map(condition =>
  //     conditionMapping[condition] || condition
  //   );
  //
  //   console.log('🔧 Backend conditions (mapped):', backendFilters.conditions);
  // }

  // Features mapping: pool → swimming_pool, others stay the same
  if (frontendFilters.features && frontendFilters.features.length > 0) {
    const backendFeatures = frontendFilters.features.map((feature) => {
      if (feature === 'pool') return 'swimming_pool';
      return feature; // parking, garden, terrace stay the same
    });
    backendFilters.characteristics = {
      must_have: backendFeatures
    };
  }

  return backendFilters;
};

/**
 * Convert backend parsed_json.properties_data to frontend filter format
 * Also handles location_data from session.parsed_json.location_data
 */
export const backendToFrontendFilters = (backendData, sessionLocationData = null) => {
  console.log('🔄 backendToFrontendFilters - Input:', backendData);
  console.log('🔄 backendToFrontendFilters - Location Data:', sessionLocationData);

  if (!backendData) {
    console.log('⚠️ backendToFrontendFilters - No data provided');
    return {};
  }

  const frontendFilters = {};

  // Location mapping - check multiple possible sources in priority order
  if (backendData.location) {
    frontendFilters.location = backendData.location;
  } else if (backendData.city) {
    frontendFilters.location = backendData.city;
  } else if (sessionLocationData?.city) {
    // Extract from session.parsed_json.location_data.city
    frontendFilters.location = sessionLocationData.city;
  } else if (sessionLocationData?.area) {
    // Extract from session.parsed_json.location_data.area
    frontendFilters.location = sessionLocationData.area;
  } else if (sessionLocationData?.name) {
    // Extract from session.parsed_json.location_data.name
    frontendFilters.location = sessionLocationData.name;
  }

  console.log('📍 Extracted location:', frontendFilters.location);

  // Price mapping
  if (backendData.price_from) {
    frontendFilters.priceMin = Number(backendData.price_from);
  }
  if (backendData.price_to) {
    frontendFilters.priceMax = Number(backendData.price_to);
  }

  // Bedrooms mapping - check multiple possible fields
  if (backendData.bedrooms_from) {
    frontendFilters.bedrooms = String(backendData.bedrooms_from);
  } else if (backendData.bedrooms) {
    frontendFilters.bedrooms = String(backendData.bedrooms);
  } else if (backendData.bedrooms_to) {
    frontendFilters.bedrooms = String(backendData.bedrooms_to);
  }

  // Bathrooms mapping - check multiple possible fields
  if (backendData.bathrooms_from) {
    frontendFilters.bathrooms = String(backendData.bathrooms_from);
  } else if (backendData.bathrooms) {
    frontendFilters.bathrooms = String(backendData.bathrooms);
  } else if (backendData.bathrooms_to) {
    frontendFilters.bathrooms = String(backendData.bathrooms_to);
  }

  // Area mapping
  if (backendData.total_area_from) {
    frontendFilters.areaMin = Number(backendData.total_area_from);
  }
  if (backendData.total_area_to) {
    frontendFilters.areaMax = Number(backendData.total_area_to);
  }

  // Property types mapping: 'apartment' → 'flat', keep others as-is
  if (backendData.types && Array.isArray(backendData.types) && backendData.types.length > 0) {
    frontendFilters.propertyTypes = backendData.types.map((type) => {
      if (type === 'apartment') return 'flat';
      return type; // villa, house, townhouse, penthouse stay the same
    });
  }

  // Conditions mapping - check multiple possible fields and reverse map from Casafari values
  console.log('🔄 Checking backend conditions:', {
    conditions: backendData.conditions,
    condition: backendData.condition
  });

  const reverseConditionMapping = {
    new: 'new',
    'very-good': 'excellent',
    used: 'good',
    ruin: 'needs_renovation'
  };

  let backendConditions = [];
  if (
    backendData.conditions &&
    Array.isArray(backendData.conditions) &&
    backendData.conditions.length > 0
  ) {
    backendConditions = backendData.conditions;
  } else if (
    backendData.condition &&
    Array.isArray(backendData.condition) &&
    backendData.condition.length > 0
  ) {
    backendConditions = backendData.condition;
  } else if (typeof backendData.conditions === 'string') {
    backendConditions = [backendData.conditions];
  } else if (typeof backendData.condition === 'string') {
    backendConditions = [backendData.condition];
  }

  if (backendConditions.length > 0) {
    frontendFilters.conditions = backendConditions.map(
      (condition) => reverseConditionMapping[condition] || condition
    );
    console.log('✅ Loaded conditions (mapped):', frontendFilters.conditions);
  } else {
    console.log('⚠️ No conditions found in backend data');
  }

  // Features mapping: swimming_pool → pool, others stay the same
  if (
    backendData.characteristics?.must_have &&
    Array.isArray(backendData.characteristics.must_have)
  ) {
    frontendFilters.features = backendData.characteristics.must_have.map((feature) => {
      if (feature === 'swimming_pool') return 'pool';
      return feature; // parking, garden, terrace stay the same
    });
  }

  console.log('✅ backendToFrontendFilters - Output:', frontendFilters);
  return frontendFilters;
};

const filterMappings = {
  frontendToBackendFilters,
  backendToFrontendFilters
};

export default filterMappings;
